import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/** A small circled-"i" info button that reveals an inline popover on hover
 *  (or click / focus for touch + keyboard). The panel renders in a portal
 *  with fixed positioning anchored to the trigger, so it escapes the wizard
 *  card's `overflow: hidden`. It portals into the `.aurora-page` ancestor
 *  (NOT document.body) because the paper/ink design tokens are scoped to
 *  `.aurora-page`, not `:root` — portaling to body would strip the panel's
 *  background/border/font.
 *
 *  Dismissal: once the pointer leaves BOTH the trigger and the panel, it
 *  fades out after a 3s grace (re-entering either one cancels it) — no click
 *  needed. Outside-click and Escape close it immediately. */
const CLOSE_GRACE_MS = 3000;
const FADE_MS = 280;

export function InfoPopover({
  label,
  align = "right",
  defaultOpen = false,
  children,
}: {
  label: string;
  /** Which edge of the trigger the panel hangs from. */
  align?: "left" | "right";
  /** Render open on mount — for isolated render tests only. */
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [leaving, setLeaving] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );
  const [container, setContainer] = useState<Element | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setLeaving(false);
  };
  // Pointer left the trigger/panel: hold for the grace window, then fade + unmount.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      setLeaving(true);
      closeTimer.current = window.setTimeout(() => {
        setOpen(false);
        setLeaving(false);
      }, FADE_MS);
    }, CLOSE_GRACE_MS);
  };
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };
  useEffect(() => () => cancelClose(), []);

  useLayoutEffect(() => {
    if (!open) return;
    const el = triggerRef.current;
    if (!el) return;
    // Portal into the token-scoped ancestor so var(--paper-3) etc. resolve.
    setContainer(el.closest(".aurora-page") ?? document.body);
    const r = el.getBoundingClientRect();
    const PANEL_W = 290;
    const rawLeft = align === "left" ? r.left : r.right - PANEL_W;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - PANEL_W - 8));
    setCoords({ top: r.bottom + 9, left });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) {
        return;
      }
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className="info-popover">
      <button
        ref={triggerRef}
        type="button"
        className="info-popover-trigger"
        aria-label={label}
        aria-expanded={open}
        onClick={() => {
          cancelClose();
          setOpen((v) => !v);
        }}
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        onFocus={openNow}
        onBlur={scheduleClose}
      >
        i
      </button>
      {open &&
        coords &&
        container &&
        createPortal(
          <div
            ref={panelRef}
            className={
              `info-popover-panel info-popover-panel--${align}` +
              (leaving ? " is-leaving" : "")
            }
            role="dialog"
            aria-label={label}
            style={{ position: "fixed", top: coords.top, left: coords.left }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {children}
          </div>,
          container,
        )}
    </span>
  );
}
